import { FC, useCallback, useMemo, useState } from 'react';
import {
    Badge,
    Box,
    Button,
    Checkbox,
    Divider,
    IconButton,
    Link,
    Popover,
    Stack,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import AlarmOutlinedIcon from '@mui/icons-material/AlarmOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import dayjs from 'dayjs';
import { useHistory } from 'react-router-dom';

import remindersApi from '../../api/reminders/api';
import type { Reminder } from '../../api/reminders/types';
import useLiveRefresh from '../HeaderIndicators/useLiveRefresh';
import { displayToast } from '../RTToast';
import ReminderDuePopup from './ReminderDuePopup';
import ReminderEditDialog from './ReminderEditDialog';

/** Completed reminders shown in the panel — the newest few; older ones stay in the database. */
const COMPLETED_SHOWN = 15;

/**
 * Due = open, and either the server has fired it or its time has passed by this computer's clock.
 * The server fires by the database's clock (reminders.scheduler.ts) and then tells this tab; going
 * by the computer's clock alone, a PC running a minute or two slow saw the fired reminder as "not
 * yet" and opened the pop-up only after its own clock and the next minute's poll caught up — the
 * delay seen on PROD on 01/11, where the server had fired within 30 seconds.
 */
const isDue = (r: Reminder, now: number) => !r.completedAt && (!!r.firedAt || Date.parse(r.remindAt) <= now);

/**
 * The header ⏰: the user's personal reminders, like iPhone Reminders. The badge counts the open
 * reminders whose time has come; the panel lists Due / Scheduled / Completed with a quick-add
 * box, a checkbox to complete, and edit / delete on every row. The due pop-up is mounted here so
 * it opens on every page.
 *
 * Re-reads on load, on tab focus, once a minute, and on the server's 'reminders:due' (the
 * scheduler fired one) and 'reminders:changed' (another tab changed one) — see useLiveRefresh.
 */
const RemindersIndicator: FC = () => {
    const history = useHistory();
    const [reminders, setReminders] = useState<Reminder[]>([]);
    const [now, setNow] = useState(() => Date.now());
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [quickText, setQuickText] = useState('');
    const [editing, setEditing] = useState<Reminder | null | undefined>(undefined); // undefined = closed
    const [confirmDelete, setConfirmDelete] = useState<number | null>(null);

    const refresh = useCallback(() => {
        setNow(Date.now());
        remindersApi
            .list()
            .then(setReminders)
            .catch(() => {});
    }, []);
    useLiveRefresh(refresh, ['reminders:due', 'reminders:changed']);

    const { due, scheduled, completed } = useMemo(() => {
        const open = reminders.filter((r) => !r.completedAt);
        return {
            due: open.filter((r) => isDue(r, now)),
            scheduled: open.filter((r) => !isDue(r, now)),
            completed: reminders.filter((r) => r.completedAt).slice(0, COMPLETED_SHOWN)
        };
    }, [reminders, now]);

    const run = async (action: () => Promise<unknown>, failure: string) => {
        try {
            await action();
            refresh();
        } catch (e: any) {
            displayToast('error', failure, e?.response?.data?.message || 'Something went wrong');
        }
    };

    /** Quick add: the text, due at the next whole hour — open the row's edit to set another time. */
    const quickAdd = () => {
        const text = quickText.trim();
        if (!text) return;
        setQuickText('');
        run(
            () => remindersApi.create({ text, remindAt: dayjs().add(1, 'hour').startOf('hour').toISOString() }),
            'Reminder not saved'
        );
    };

    const row = (r: Reminder) => (
        <Box key={r._id} sx={{ display: 'flex', alignItems: 'flex-start', px: 1, py: 0.75, gap: 0.5 }}>
            <Checkbox
                size="small"
                checked={!!r.completedAt}
                onChange={(e) =>
                    run(() => remindersApi.setCompleted(r._id, e.target.checked), 'Could not update the reminder')
                }
                sx={{ p: 0.5 }}
                inputProps={{ 'aria-label': r.completedAt ? 'Mark not done' : 'Mark done' }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
                {/* dir="auto": Hebrew reads right-to-left, English left-to-right, per reminder. */}
                <Typography
                    variant="body2"
                    dir="auto"
                    sx={{
                        textAlign: 'start',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        textDecoration: r.completedAt ? 'line-through' : 'none',
                        color: r.completedAt ? 'text.secondary' : 'text.primary'
                    }}
                >
                    {r.text}
                </Typography>
                <Typography
                    variant="caption"
                    sx={{ color: isDue(r, now) ? 'error.main' : 'text.secondary', display: 'block' }}
                >
                    {dayjs(r.remindAt).format('DD/MM/YYYY HH:mm')}
                    {r.travelFileRef && (
                        <>
                            {' · '}
                            <Link
                                component="button"
                                variant="caption"
                                onClick={() => {
                                    setAnchorEl(null);
                                    history.push(
                                        `/reservations/tour/travel-file-list/travel-file?ref_number=${r.travelFileRef}`
                                    );
                                }}
                            >
                                TF {r.travelFileRef}
                            </Link>
                        </>
                    )}
                </Typography>
                {confirmDelete === r._id && (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                        <Typography variant="caption">Delete this reminder?</Typography>
                        <Button
                            size="small"
                            color="error"
                            onClick={() => {
                                setConfirmDelete(null);
                                run(() => remindersApi.remove(r._id), 'Could not delete the reminder');
                            }}
                        >
                            Delete
                        </Button>
                        <Button size="small" onClick={() => setConfirmDelete(null)}>
                            Cancel
                        </Button>
                    </Stack>
                )}
            </Box>
            {!r.completedAt && (
                <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => setEditing(r)} aria-label="Edit reminder">
                        <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            )}
            <Tooltip title="Delete">
                <IconButton size="small" onClick={() => setConfirmDelete(r._id)} aria-label="Delete reminder">
                    <DeleteOutlineIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        </Box>
    );

    const section = (title: string, items: Reminder[], color?: string) =>
        items.length ? (
            <>
                <Typography
                    variant="overline"
                    sx={{ px: 2, pt: 1, display: 'block', lineHeight: 1.8, color: color || 'text.secondary' }}
                >
                    {title} ({items.length})
                </Typography>
                {items.map(row)}
            </>
        ) : null;

    return (
        <>
            <IconButton
                size="small"
                onClick={(e) => {
                    setAnchorEl(e.currentTarget);
                    refresh();
                }}
                title="Reminders"
                aria-label="Reminders"
                sx={{ color: 'inherit' }}
            >
                <Badge
                    badgeContent={due.length}
                    color="error"
                    max={99}
                    sx={{ '& .MuiBadge-badge': { fontSize: 10, height: 16, minWidth: 16 } }}
                >
                    <AlarmOutlinedIcon />
                </Badge>
            </IconButton>
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={() => {
                    setAnchorEl(null);
                    setConfirmDelete(null);
                }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Box sx={{ width: 320 }}>
                    <Stack direction="row" alignItems="center" sx={{ px: 2, pt: 1.5, pb: 1 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', flex: 1 }}>Reminders</Typography>
                        <Button size="small" onClick={() => setEditing(null)}>
                            + New
                        </Button>
                    </Stack>
                    <Box sx={{ px: 2, pb: 1.5 }}>
                        <TextField
                            size="small"
                            fullWidth
                            placeholder="Quick reminder — Enter adds it for the next hour"
                            value={quickText}
                            onChange={(e) => setQuickText(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    quickAdd();
                                }
                            }}
                            inputProps={{ maxLength: 500, dir: 'auto' }}
                        />
                    </Box>
                    <Divider />
                    <Box sx={{ maxHeight: '55vh', overflowY: 'auto', pb: 1 }}>
                        {!due.length && !scheduled.length && !completed.length ? (
                            <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 3, textAlign: 'center' }}>
                                No reminders yet
                            </Typography>
                        ) : (
                            <>
                                {section('Due', due, 'error.main')}
                                {section('Scheduled', scheduled)}
                                {section('Completed', completed)}
                            </>
                        )}
                    </Box>
                </Box>
            </Popover>
            <ReminderEditDialog
                open={editing !== undefined}
                reminder={editing || null}
                onClose={() => setEditing(undefined)}
                onSaved={refresh}
            />
            <ReminderDuePopup due={due} onChanged={refresh} />
        </>
    );
};

export default RemindersIndicator;
